import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import HeroSection from './components/HeroSection';
import HotVideos from './components/HotVideos';
import CategoryGrid from './components/CategoryGrid';
import LatestVideos from './components/LatestVideos';
import Features from './components/Features';
import FAQ from './components/FAQ';
import { articles } from '@/mocks/articles';

export default function Home() {
  useEffect(() => {
    document.title = '黄瓜视频在线 - 免费高清视频在线播放平台';
  }, []);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.huangguaTV.com/#organization',
        name: '黄瓜视频在线',
        url: 'https://www.huangguaTV.com',
        description: '黄瓜视频在线为您提供海量高清视频免费在线观看服务，无需下载，随时随地畅享优质视频内容。',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.huangguaTV.com/logo.png',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-000-0000',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
      {
        '@type': 'WebSite',
        '@id': 'https://www.huangguaTV.com/#website',
        url: 'https://www.huangguaTV.com',
        name: '黄瓜视频在线',
        inLanguage: 'zh-CN',
        publisher: { '@id': 'https://www.huangguaTV.com/#organization' },
      },
      {
        '@type': 'WebPage',
        '@id': 'https://www.huangguaTV.com/#webpage',
        url: 'https://www.huangguaTV.com/',
        name: '黄瓜视频在线 - 免费高清视频在线播放平台',
        description: '黄瓜视频在线为您提供海量高清视频免费在线观看服务，无需下载，随时随地畅享优质视频内容。黄瓜视频在线支持多终端播放，画质清晰，加载流畅，是您在线追剧、看综艺、看短视频的首选平台。',
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://www.huangguaTV.com/#website' },
        about: { '@id': 'https://www.huangguaTV.com/#organization' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.huangguaTV.com/',
            },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '黄瓜视频在线需要付费吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '完全不需要！黄瓜视频在线是一款完全免费的在线视频播放平台。所有视频内容均对用户免费开放，无任何隐藏收费。',
            },
          },
          {
            '@type': 'Question',
            name: '黄瓜视频在线支持哪些设备观看？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '黄瓜视频在线支持多终端播放，包括电脑端、手机端、平板电脑、智能电视等。',
            },
          },
          {
            '@type': 'Question',
            name: '黄瓜视频在线的视频画质如何？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '黄瓜视频在线支持从流畅到4K超清的多档画质选择，大部分热门内容已支持4K超清画质，配合HDR技术呈现细腻画面。',
            },
          },
          {
            '@type': 'Question',
            name: '黄瓜视频在线的内容是否正版？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的！黄瓜视频在线与众多内容提供商建立了正式合作关系，平台上的所有视频内容均获得了合法的播放授权。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />

      <Navbar />

      <main>
        <article>
          <HeroSection />
        </article>
        <article>
          <HotVideos />
        </article>
        <article>
          <CategoryGrid />
        </article>
        <article>
          <LatestVideos />
        </article>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                最新资讯
              </h2>
              <p className="text-sm text-foreground-500">了解黄瓜视频在线的最新动态和观影指南</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/category/zixun"
                className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full text-sm font-medium bg-secondary-500 text-white hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <article>
          <Features />
        </article>
        <article>
          <FAQ />
        </article>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-primary-500">
          <div className="max-w-[700px] mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
              准备好开始您的观影之旅了吗？
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-8">
              海量高清视频免费看，现在就加入黄瓜视频在线，和5亿用户一起畅享精彩内容
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/dianshiju"
                className="px-8 py-3 rounded-full text-base font-medium bg-white text-primary-600 hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line mr-1.5"></i>立即观看
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-full text-base font-medium bg-white/15 text-white hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}